import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Calendar, FileText, AlertTriangle, X } from 'lucide-react';
import ApplicantPackModal from './ApplicantPackModal';

const PIPELINE_STAGES = [
    { id: 'Matched', title: 'Matched', color: 'bg-indigo-50 border-indigo-200' },
    { id: 'Shortlisted', title: 'Shortlisted', color: 'bg-blue-50 border-blue-200' },
    { id: 'Interview Scheduled', title: 'Interview Scheduled', color: 'bg-amber-50 border-amber-200' },
    { id: 'Interviewed', title: 'Interviewed', color: 'bg-purple-50 border-purple-200' },
    { id: 'Offer', title: 'Offer Sent', color: 'bg-emerald-50 border-emerald-200' },
    { id: 'Accepted', title: 'Accepted', color: 'bg-green-50 border-green-200' },
    { id: 'Declined', title: 'Declined', color: 'bg-red-50 border-red-200' },
    { id: 'Hired', title: 'Hired', color: 'bg-slate-50 border-slate-200' },
];

const getBandColor = (band) => {
    switch (band) {
        case 'Strong': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
        case 'Good': return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'Partial': return 'bg-amber-100 text-amber-800 border-amber-200';
        case 'Not a Match': return 'bg-rose-100 text-rose-800 border-rose-200';
        // 'Not scored' and anything unknown stay neutral — a grey chip must not
        // read as a graded result.
        default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
};

/**
 * @param onViewCandidate optional. When given, clicking a card delegates to the
 *        parent instead of opening the built-in modal. Callers that hold a
 *        richer applicant object (job details, matchData, college) should use
 *        this — the board only receives a flattened display shape, and feeding
 *        that to the pack renders it with blank job context.
 */
const PipelineBoard = ({ applications, onStatusUpdate, onRefresh, onViewCandidate, readOnly = false }) => {
    const [columns, setColumns] = useState({});
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [confirmDrag, setConfirmDrag] = useState(null);

    // Initialize columns
    useEffect(() => {
        const newCols = {};
        PIPELINE_STAGES.forEach(stage => {
            newCols[stage.id] = [];
        });

        const normalizeStatus = (s) => {
            if (!s) return 'Matched';
            const mapped = PIPELINE_STAGES.find(st => st.id.toLowerCase() === s.toLowerCase());
            if (mapped) return mapped.id;
            
            if (['applied', 'pending', 'under review'].includes(s.toLowerCase())) return 'Matched';
            if (['interviewing'].includes(s.toLowerCase())) return 'Interviewed';
            if (['selected', 'placed', 'offered', 'in progress'].includes(s.toLowerCase())) return 'Offer';
            if (['rejected'].includes(s.toLowerCase())) return 'Declined';
            
            return 'Matched'; 
        };

        if (applications && applications.length > 0) {
            applications.forEach(app => {
                const s = normalizeStatus(app.status);
                if (newCols[s]) {
                    newCols[s].push(app);
                }
            });
        }
        
        setColumns(newCols);
    }, [applications]);

    const onDragEnd = async (result) => {
        if (readOnly) return;
        const { source, destination, draggableId } = result;

        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const sourceCol = [...columns[source.droppableId]];
        const destCol = [...columns[destination.droppableId]];
        
        const [movedItem] = sourceCol.splice(source.index, 1);
        destCol.splice(destination.index, 0, movedItem);
        
        setConfirmDrag({
            source,
            destination,
            draggableId,
            sourceCol,
            destCol,
            movedItem,
            originalColumns: columns
        });
    };

    const handleConfirmDrag = async () => {
        if (!confirmDrag) return;
        
        setColumns({
            ...columns,
            [confirmDrag.source.droppableId]: confirmDrag.sourceCol,
            [confirmDrag.destination.droppableId]: confirmDrag.destCol
        });

        const destId = confirmDrag.destination.droppableId;
        const dragId = confirmDrag.draggableId;
        setConfirmDrag(null);

        try {
            await onStatusUpdate(dragId, destId);
        } catch (error) {
            onRefresh();
        }
    };

    const handleCancelDrag = () => {
        setConfirmDrag(null);
    };

    return (
        <div className="flex h-[calc(100vh-200px)] overflow-x-auto pb-4 gap-4">
            <DragDropContext onDragEnd={onDragEnd}>
                {PIPELINE_STAGES.map(stage => (
                    <div key={stage.id} className={`flex-shrink-0 w-80 rounded-xl border ${stage.color} flex flex-col`}>
                        <div className="p-3 border-b border-black/5 font-semibold text-sm flex justify-between items-center text-slate-800">
                            <span>{stage.title}</span>
                            <span className="bg-white rounded-full px-2 py-0.5 text-xs shadow-sm">
                                {columns[stage.id]?.length || 0}
                            </span>
                        </div>
                        
                        <Droppable droppableId={stage.id}>
                            {(provided, snapshot) => (
                                <div 
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`flex-1 p-2 overflow-y-auto ${snapshot.isDraggingOver ? 'bg-black/5' : ''}`}
                                >
                                    {columns[stage.id]?.map((app, index) => {
                                        const skillMatch = app.matchData?.skillMatch || {};
                                        const academicMatch = app.matchData?.academicMatch || {};
                                        // No requirements on the role means there was nothing to score,
                                        // which is different from scoring zero.
                                        const isUnscored = skillMatch.notScored === true
                                            || !(skillMatch.breakdown?.skillContributions || []).length;

                                        return (
                                            <Draggable key={app._id} draggableId={app._id} index={index} isDragDisabled={readOnly}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={`bg-white p-3 mb-2 rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer ${snapshot.isDragging ? 'shadow-lg ring-2 ring-indigo-500' : ''}`}
                                                        onClick={() => (onViewCandidate ? onViewCandidate(app) : setSelectedApplicant(app))}
                                                    >
                                                        <div className="flex justify-between items-start mb-2">
                                                            <div>
                                                                <h4 className="font-medium text-slate-800 text-sm truncate w-48">{app.fullName}</h4>
                                                                <p className="text-xs text-slate-500 truncate w-48">{app.collegeName}</p>
                                                            </div>
                                                            {app.profileImage ? (
                                                                <img src={app.profileImage} alt="" className="w-8 h-8 rounded-full border border-slate-200 object-cover" />
                                                            ) : (
                                                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-medium uppercase">
                                                                    {app.fullName?.charAt(0) || '?'}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {/* Skills. "0% Not scored" was contradictory — when the role
                                                                defines no requirements there is no percentage to show. */}
                                                            {skillMatch.band && (
                                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-md border font-medium ${getBandColor(skillMatch.band)}`}>
                                                                    {isUnscored
                                                                        ? 'Skills not scored'
                                                                        : `Skills ${skillMatch.score}% · ${skillMatch.band}`}
                                                                </span>
                                                            )}
                                                            {/* isEligible covers ACADEMIC gates only (CGPA, backlogs, 10th/12th,
                                                                degree) — never skills. A bare "Eligible" read as a verdict on
                                                                the whole application, next to a 0% skills match. */}
                                                            {academicMatch.isEligible !== undefined && (
                                                                <span
                                                                    title={academicMatch.isEligible
                                                                        ? 'Meets the academic criteria for this role'
                                                                        : (academicMatch.failedCriteria || []).join(' · ') || 'Does not meet the academic criteria'}
                                                                    className={`text-[10px] px-1.5 py-0.5 rounded-md border font-medium ${academicMatch.isEligible ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                                                                >
                                                                    {academicMatch.isEligible ? 'Academic ✓' : 'Academic ✗'}
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className="mt-3 flex items-center justify-between text-slate-400">
                                                            <div className="flex space-x-2">
                                                                {app.resumeUrl && <FileText size={14} className="hover:text-indigo-600" />}
                                                                {app.interviewDetails?.date && <Calendar size={14} className="text-amber-500" />}
                                                            </div>
                                                            <span className="text-[10px]">
                                                                {app.appliedAt ? new Date(app.appliedAt).toLocaleDateString() : 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                ))}
            </DragDropContext>

            {selectedApplicant && (
                <ApplicantPackModal 
                    isOpen={!!selectedApplicant} 
                    onClose={() => setSelectedApplicant(null)}
                    applicant={selectedApplicant}
                    onStatusUpdate={(status, payload) => {
                        // Forward the extra payload (offer package/date/signature)
                        // instead of dropping it — the offer dialog depends on it.
                        onStatusUpdate(selectedApplicant._id, status, payload);
                        setSelectedApplicant(null);
                        onRefresh();
                    }}
                />
            )}

            {confirmDrag && (
                <div
                    // lg:pl-[272px] = sidebar width (w-64) + p-4, so the dialog centers
                    // over the visible content area, not the full viewport
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 lg:pl-[272px]"
                    onClick={handleCancelDrag}
                >
                    <div
                        className="bg-white dark:bg-[#002147] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-slate-200 dark:border-[#1a3884]/40"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-8 text-center">
                            {/* Icon */}
                            <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 flex items-center justify-center mb-4">
                                <AlertTriangle className="text-amber-500" size={26} />
                            </div>

                            {/* Title & candidate */}
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Update Status</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
                                You are about to move
                                {confirmDrag.movedItem?.fullName && (
                                    <span className="font-semibold text-slate-800 dark:text-white"> {confirmDrag.movedItem.fullName}</span>
                                )}
                                {!confirmDrag.movedItem?.fullName && ' this candidate'} to a new pipeline stage.
                            </p>

                            {/* Stage transition */}
                            <div className="flex items-center justify-center gap-3 mt-5 bg-slate-50 dark:bg-[#001733] border border-slate-200 dark:border-[#1a3884]/30 rounded-xl py-3 px-4">
                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 bg-white dark:bg-[#002147] border border-slate-200 dark:border-[#1a3884]/40 rounded-lg px-3 py-1.5 truncate max-w-[10rem]">
                                    {confirmDrag.source.droppableId}
                                </span>
                                <span className="text-slate-400 dark:text-slate-500 flex-shrink-0" aria-hidden="true">→</span>
                                <span className="text-xs font-bold text-white bg-[#1a3884] rounded-lg px-3 py-1.5 shadow-sm truncate max-w-[10rem]">
                                    {confirmDrag.destination.droppableId}
                                </span>
                            </div>

                            {/* Actions */}
                            <div className="grid grid-cols-2 gap-3 mt-7">
                                <button
                                    onClick={handleCancelDrag}
                                    className="w-full px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-transparent border border-slate-300 dark:border-[#1a3884]/50 hover:bg-slate-50 dark:hover:bg-[#001733] rounded-xl transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmDrag}
                                    autoFocus
                                    className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-[#1a3884] hover:bg-[#142c68] rounded-xl shadow-sm shadow-[#1a3884]/25 transition-colors"
                                >
                                    Confirm Move
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PipelineBoard;
